import { useState } from 'react';
import { FiCheck, FiTag, FiX } from 'react-icons/fi';
import SummaryApi from '../common';
import { request } from '../helpers/request';

const CouponInput = ({ onCouponApply, cartTotal }) => {
    const [couponCode, setCouponCode] = useState('');
    const [appliedCoupon, setAppliedCoupon] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const validateCoupon = async () => {
        if (!couponCode.trim()) {
            setError('Please enter a coupon code');
            return;
        }

        setLoading(true);
        setError('');

        try {
            const response = await request(SummaryApi.validateCoupon, {
                body: JSON.stringify({
                    code: couponCode.toUpperCase(),
                    orderAmount: cartTotal
                })
            });

            const data = await response.json();

            if (data.success) {
                setAppliedCoupon(data.data);
                setError('');
                onCouponApply(data.data);
            } else {
                setError(data.message || 'Invalid coupon code');
                setAppliedCoupon(null);
                onCouponApply(null);
            }
        } catch (err) {
            console.error(err);
            setError('Failed to validate coupon. Please try again.');
            setAppliedCoupon(null);
            onCouponApply(null);
        } finally {
            setLoading(false);
        }
    };

    const removeCoupon = () => {
        setCouponCode('');
        setAppliedCoupon(null);
        setError('');
        onCouponApply(null);
    };

    return (
        <div className="bg-white rounded-lg shadow-sm p-4 mb-4">
            <div className="flex items-center gap-2 mb-3">
                <FiTag className="text-red-600 text-xl" />
                <h3 className="font-semibold text-gray-800">Apply Coupon</h3>
            </div>

            {!appliedCoupon ? (
                <div className="space-y-3">
                    <div className="flex gap-2">
                        <input
                            type="text"
                            value={couponCode}
                            onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                            placeholder="Enter coupon code"
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 uppercase"
                            disabled={loading}
                        />
                        <button
                            onClick={validateCoupon}
                            disabled={loading || !couponCode.trim()}
                            className="px-6 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors font-medium"
                        >
                            {loading ? 'Validating...' : 'Apply'}
                        </button>
                    </div>

                    {error && (
                        <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-md">
                            <span className="text-red-600 text-sm">{error}</span>
                        </div>
                    )}
                </div>
            ) : (
                <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-md">
                        <div className="flex items-center gap-2">
                            <FiCheck className="text-green-600" />
                            <div>
                                <p className="font-semibold text-green-800">{appliedCoupon.code}</p>
                                <p className="text-sm text-green-700">
                                    {appliedCoupon.discountType === 'PERCENTAGE' 
                                        ? `${appliedCoupon.discountValue}% off` 
                                        : `₹${appliedCoupon.discountValue} off`}
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            <span className="font-bold text-green-700">
                                -₹{appliedCoupon.discountAmount.toFixed(2)}
                            </span>
                            <button
                                onClick={removeCoupon}
                                className="p-1 hover:bg-green-100 rounded-full transition-colors"
                                title="Remove coupon"
                            >
                                <FiX className="text-green-600" />
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CouponInput;
